import {useAxios} from "../utils/hooks";
import {Button, Col, Form, Input, Row, Select, Spin} from "antd";
import useSafeState from "../utils/safe-state";
import React, {useEffect} from "react";
import {openNotification} from "../utils/openNotification";
import {Trans} from "@lingui/macro";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
    itemId?: number;
}

const ContactDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);

    const handleEdit = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            const contactData: any = {
                id: data.id,
                type: values.type,
                name: values.name,
                postalAddress: values.postalAddress,
                emailAddress: values.emailAddress,
                shippingAddress: values.shippingAddress,
            };
            
            const itemId = props.itemId || data.itemId || (data.item && data.item.id);
            if (itemId) {
                contactData.item = { id: itemId };
            } else {
                contactData.supplier = data.supplier || { code: localStorage.getItem('supplierCode') };
            }
            
            axiosInstance.current
                .put(
                    '/resource/contacts/' + encodeURIComponent(data.id),
                    contactData,
                    config,
                )
                .then(() => {
                    openNotification('success', 'Contact successfully updated.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        form.resetFields();

        if(props.mode === 'Edit') {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if ((key === 'supplier' || key === 'item') && value && typeof value === 'object') {
                    continue;
                }
                fieldsValues.push({name: key, value: value});
            }
            form.setFields(fieldsValues);
        }
    }, [props.mode, data]);

    const handleFinish = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            const contactData: any = {
                type: values.type,
                name: values.name,
                postalAddress: values.postalAddress,
                emailAddress: values.emailAddress,
                shippingAddress: values.shippingAddress,
            };
            
            const itemId = props.itemId || data.itemId;
            if (itemId) {
                contactData.item = { id: itemId };
            } else {
                const supplierCode = localStorage.getItem('supplierCode');
                contactData.supplier = { code: supplierCode };
            }
            
            axiosInstance.current
                .post(
                    '/resource/contacts',
                    contactData,
                    config,
                )
                .then(() => {
                    openNotification('success', 'Contact successfully created.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    const onReset = () => {
        form.resetFields();
    };

    return (
        <Form
            id="newContact"
            layout='vertical'
            form={form}
            onFinish={(values) => (props.mode === 'Edit' ? handleEdit(values) : handleFinish(values))}
        >
            <Spin spinning={loading}>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="type"
                            label={<Trans>Type</Trans>}
                            rules={[{
                                required: true,
                                message: <Trans>Please select the type</Trans>
                            }]}
                        >
                            <Select placeholder={<Trans>Select type</Trans>}>
                                <Select.Option value="DEPARTURE">
                                    <Trans>DEPARTURE</Trans>
                                </Select.Option>
                                <Select.Option value="ARRIVAL">
                                    <Trans>ARRIVAL</Trans>
                                </Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label={<Trans>Name of business or person</Trans>}
                            rules={[{
                                required: true,
                                message: <Trans>Please enter the name</Trans>
                            }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="postalAddress"
                            label={<Trans>Postal Address</Trans>}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="emailAddress"
                            label={<Trans>Email Address</Trans>}
                            rules={[{
                                type: "email",
                                message: <Trans>Please enter a valid email address</Trans>
                            }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="shippingAddress"
                            label={<Trans>Shipping Address</Trans>}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div style={{ marginTop: 16 }}>
                {props.mode === 'Edit'
                    ? (<Button htmlType="submit" type="primary">
                        <Trans>Save</Trans>
                    </Button>)
                    :
                    <>
                        <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                            <Trans>Clear</Trans>
                        </Button>
                        <Button
                            loading={loading}
                            htmlType="submit"
                            type="primary"
                            style={{ marginRight: 8 }}
                        >
                            <Trans>Save</Trans>
                        </Button>
                    </>
                }
            </div>
        </Form>
    );

}
export default ContactDrawer;
